import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Marked } from 'marked';
import { moderatorGuideMarkdown } from '../lib/moderator-guide';
import { calloutExtension } from '../lib/guide-callout-extension';
import { kbdExtension } from '../lib/guide-kbd-extension';
import { GUIDE_DEMOS } from './GuideExamples';

/**
 * Slug a heading text the same way GitHub-flavoured markdown anchors them:
 * lowercase, non-alphanumeric → dash, collapsed dashes, trimmed. Used so the
 * sidebar TOC links can `#scroll` into matching `<h2>` ids that we inject
 * into the rendered HTML via a marked renderer override.
 */
function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Strip HTML tags from a string. The default marked heading renderer hands us
 * tokenised HTML (e.g. `<code>…</code>` inside a heading), but the TOC slug
 * needs the plain-text form. Tiny helper rather than a parser since the input
 * comes from our own markdown and is well-formed.
 */
function stripHtml(html: string): string {
  return html.replace(/<[^>]+>/g, '');
}

// G-2 / G-3: a scoped Marked instance with the GitHub-style admonition
// extension and the inline `[[Cmd+K]]` → `<kbd>` extension registered.
// Using a per-module instance (rather than mutating the global `marked`)
// keeps the extension's render side-effects local to the guide and
// avoids leaking into any future markdown render calls.
//
// G-4: we also override the heading renderer to stamp slug ids inline at
// parse time. Doing this in the HTML (rather than a post-mount useEffect)
// means React's reconciler never wipes the ids on re-render — important
// because the scroll-spy effect calls setState which would otherwise drop
// the post-mount-stamped ids when React re-applied dangerouslySetInnerHTML.
const guideMarked = new Marked({
  extensions: [calloutExtension, kbdExtension],
});
guideMarked.use({
  renderer: {
    heading(text: string, level: number) {
      // `text` arrives as already-rendered inline HTML; strip tags to
      // generate a clean slug, but preserve the rendered HTML in the
      // heading body so inline code/em/etc still render correctly.
      const id = slugify(stripHtml(text));
      return `<h${level} id="${id}">${text}</h${level}>\n`;
    },
  },
});

interface TocEntry {
  id: string;
  text: string;
}

/**
 * Read the raw markdown source to pull out the `## ...` headings up-front so
 * the TOC matches the body exactly. Doing this off the raw source (rather
 * than the rendered HTML) keeps the render path simple and means the TOC
 * works server-side / in tests even if marked's html ever changes shape.
 */
function extractTocFromMarkdown(md: string): TocEntry[] {
  const entries: TocEntry[] = [];
  for (const line of md.split('\n')) {
    const match = /^##\s+(.+?)\s*$/.exec(line);
    if (match) {
      const text = match[1].trim();
      entries.push({ id: slugify(text), text });
    }
  }
  return entries;
}

/**
 * Quick-start cards. Each card is an anchor link to a guide section so a
 * first-time moderator can land on the most relevant chunk of the guide
 * without scrolling. The `anchor` value matches the slug a `## Heading`
 * would resolve to via {@link slugify}.
 */
interface QuickStartCard {
  badge: string;
  title: string;
  summary: string;
  anchor: string;
}

const QUICK_START_CARDS: QuickStartCard[] = [
  {
    badge: '1',
    title: 'First time signing in',
    summary:
      'Cognito sign-in, password reset, TOTP MFA setup. Read this before your first shift.',
    anchor: 'access',
  },
  {
    badge: '2',
    title: 'Approving a clean submission',
    summary:
      'Read the AI verdict, scan the media, apply suggested themes, hit Approve.',
    anchor: 'standard-review-flow',
  },
  {
    badge: '3',
    title: 'Asking the contributor for more info',
    summary:
      'When to Request Changes, templates to choose from, what the contributor sees.',
    anchor: 'decision-standards',
  },
  {
    badge: '4',
    title: 'Handling sensitive content',
    summary:
      'Sensitivity flags, PII review, escalation triggers, and the AI rationale to defer to.',
    anchor: 'sensitivity-and-privacy',
  },
];

const GUIDE_LAST_UPDATED = 'May 2026';
const GUIDE_SOURCE_URL =
  'https://github.com/actual-damien/brisbane-pride-festival/blob/main/docs/pride-history/moderator-guide.md';

/**
 * In-tool moderator guide. Renders the markdown stored at
 * `docs/pride-history/moderator-guide.md` so moderators have one in-tool
 * reference. Sticky TOC on desktop; anchor links scroll smoothly. No edit
 * affordances — this view is read-only.
 */
export default function GuidePage() {
  const md = moderatorGuideMarkdown;

  const toc = useMemo(() => extractTocFromMarkdown(md), [md]);

  // marked is sync when given a plain string in v12; cast through unknown to
  // keep types tight without the `async`-overload signature leaking out.
  const html = useMemo(() => {
    const rendered = guideMarked.parse(md, { async: false }) as unknown;
    return typeof rendered === 'string' ? rendered : String(rendered);
  }, [md]);

  // G-5: split the rendered HTML on `{{demo:<id>}}` markers so live React
  // components can be interleaved between markdown fragments. Markers
  // arrive wrapped in `<p>{{demo:foo}}</p>` because they're on their own
  // line in the markdown source; the regex matches both the wrapped and
  // bare forms so a marker placed in a different context still resolves.
  const bodySegments = useMemo(() => {
    type Segment =
      | { kind: 'html'; html: string }
      | { kind: 'demo'; id: string };
    const segments: Segment[] = [];
    const pattern = /<p>\s*\{\{demo:([a-z0-9-]+)\}\}\s*<\/p>|\{\{demo:([a-z0-9-]+)\}\}/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = pattern.exec(html)) !== null) {
      if (match.index > lastIndex) {
        segments.push({ kind: 'html', html: html.slice(lastIndex, match.index) });
      }
      const id = match[1] || match[2];
      // Only swap in registered demos; unknown markers fall through as
      // literal text so a typo is visible in the rendered page.
      if (id && GUIDE_DEMOS[id]) {
        segments.push({ kind: 'demo', id });
      } else {
        segments.push({ kind: 'html', html: match[0] });
      }
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < html.length) {
      segments.push({ kind: 'html', html: html.slice(lastIndex) });
    }
    return segments;
  }, [html]);

  // The slug ids are stamped at parse time by the heading renderer
  // override above, so no post-mount stamping is needed. We still expose
  // a ref so the scroll-spy effect can scope its IntersectionObserver to
  // the body.
  const bodyRef = useRef<HTMLDivElement | null>(null);

  // Smooth-scroll the matched section into view when the URL hash points
  // inside the guide (e.g., `#/guide#sensitivity-and-privacy`).
  const [hashTarget, setHashTarget] = useState<string | null>(null);
  useEffect(() => {
    const readHash = () => {
      const raw = window.location.hash || '';
      const inner = raw.split('#').slice(2).join('#'); // after `#/guide#`
      setHashTarget(inner || null);
    };
    readHash();
    window.addEventListener('hashchange', readHash);
    return () => window.removeEventListener('hashchange', readHash);
  }, []);
  useEffect(() => {
    if (!hashTarget) return;
    const el = document.getElementById(hashTarget);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [hashTarget, html]);

  // G-4: scroll-spy — highlight the currently-visible section in the TOC.
  // Uses IntersectionObserver against every `<h2>` in the body. The
  // rootMargin pulls the "active" line down ~100px from the top of the
  // viewport so the highlighted entry matches what a moderator is actually
  // reading (rather than what's exiting the top).
  const [activeSection, setActiveSection] = useState<string | null>(null);
  useEffect(() => {
    const root = bodyRef.current;
    if (!root) return;
    if (typeof IntersectionObserver === 'undefined') return;
    const headings = Array.from(root.querySelectorAll('h2')) as HTMLElement[];
    if (headings.length === 0) return;
    // Pre-scroll, keep the first section highlighted so the TOC always
    // has *something* current.
    setActiveSection((prev) => prev || headings[0].id);

    // Track each heading's most-recent intersection state. On every
    // callback we pick the topmost (DOM-order earliest) heading that is
    // currently intersecting. The negative-bottom rootMargin restricts
    // "intersecting" to the top ~25% of the viewport, which is where the
    // moderator's eye naturally lands.
    const intersecting = new Set<string>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const id = (entry.target as HTMLElement).id;
          if (!id) continue;
          if (entry.isIntersecting) {
            intersecting.add(id);
          } else {
            intersecting.delete(id);
          }
        }
        // Walk headings in DOM order; the first one currently intersecting
        // is the active section.
        for (const h of headings) {
          if (intersecting.has(h.id)) {
            setActiveSection(h.id);
            return;
          }
        }
        // No heading is in the active band — leave the previous active
        // entry alone (so the TOC stays highlighted while reading body
        // copy between headings).
      },
      // Narrow band near the top of the viewport ~100..200px from the top.
      { rootMargin: '-100px 0px -75% 0px', threshold: [0, 1] },
    );
    headings.forEach((h) => observer.observe(h));
    return () => observer.disconnect();
  }, [html]);

  const navigateToAnchor = (anchor: string) => {
    window.location.hash = `#/guide#${anchor}`;
  };

  return (
    <div className="guide-page" data-testid="guide-page">
      <header className="guide-page__hero" data-testid="guide-hero">
        <div className="guide-page__hero-text">
          <h1>Moderator guide</h1>
          <p className="guide-page__subtitle">
            Everything you need to review Pride History submissions
            confidently. Skim the quick-start cards, then dip into a section.
          </p>
          <div className="guide-page__meta">
            <span className="guide-page__meta-item">
              Last updated {GUIDE_LAST_UPDATED}
            </span>
            <a
              className="guide-page__meta-item guide-page__meta-link"
              href={GUIDE_SOURCE_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="guide-edit-link"
            >
              Edit on GitHub
            </a>
          </div>
        </div>
        <div
          className="guide-page__quick-start"
          aria-label="Start here"
          role="list"
          data-testid="guide-quick-start"
        >
          {QUICK_START_CARDS.map((card) => (
            <a
              key={card.anchor}
              role="listitem"
              className="guide-quick-card"
              href={`#/guide#${card.anchor}`}
              onClick={(e) => {
                e.preventDefault();
                navigateToAnchor(card.anchor);
              }}
              data-testid={`guide-quick-card-${card.anchor}`}
            >
              <span className="guide-quick-card__badge" aria-hidden="true">
                {card.badge}
              </span>
              <span className="guide-quick-card__title">{card.title}</span>
              <span className="guide-quick-card__summary">{card.summary}</span>
            </a>
          ))}
        </div>
      </header>
      <div className="guide-page__layout">
        <nav
          className="guide-page__toc"
          aria-label="Guide sections"
          data-testid="guide-toc"
        >
          {/* G-6: mobile collapses the TOC into a <details> accordion so
              the first thing on small screens is the body copy, not a
              20-link table of contents. The summary swaps out the
              heading; on desktop the <details> stays open courtesy of
              the `[open]` attribute and the CSS resets the disclosure. */}
          <details
            className="guide-page__toc-details"
            open
            data-testid="guide-toc-details"
          >
            <summary className="guide-page__toc-summary">
              <h2>On this page</h2>
            </summary>
            <ul>
              {toc.map((entry) => {
                const isActive = entry.id === activeSection;
                return (
                  <li key={entry.id}>
                    <a
                      href={`#/guide#${entry.id}`}
                      className={
                        isActive
                          ? 'guide-page__toc-link guide-page__toc-link--active'
                          : 'guide-page__toc-link'
                      }
                      aria-current={isActive ? 'location' : undefined}
                      onClick={(e) => {
                        e.preventDefault();
                        window.location.hash = `#/guide#${entry.id}`;
                      }}
                      data-testid={`guide-toc-link-${entry.id}`}
                    >
                      {entry.text}
                    </a>
                  </li>
                );
              })}
            </ul>
          </details>
        </nav>
        <article
          className="guide-page__body"
          data-testid="guide-page-body"
          ref={bodyRef}
        >
          {/* G-5: interleave HTML fragments with live React demos so the
              guide can show real components inline at the right spot in
              the markdown. */}
          {bodySegments.map((seg, i) => {
            if (seg.kind === 'demo') {
              const factory = GUIDE_DEMOS[seg.id];
              return (
                <Fragment key={`demo-${seg.id}-${i}`}>{factory()}</Fragment>
              );
            }
            return (
              <div
                key={`html-${i}`}
                className="guide-page__body-fragment"
                dangerouslySetInnerHTML={{ __html: seg.html }}
              />
            );
          })}
        </article>
      </div>
    </div>
  );
}
